'use client';
import type { CSSProperties, MouseEventHandler, ReactElement } from 'react';
import { motion } from 'framer-motion';
import { pxToRem } from '@/theme/spacing';
import { zIndex } from '@/theme/zIndex';
import { dataEl, type DataElScope } from '@/utils/dataEl';
import { CSS_POINTER_EVENTS_AUTO, CSS_POINTER_EVENTS_NONE, NAVBAR_HEIGHT, } from './Cpagelayout.constants';
import { LoggedNavbarLeftAction } from './LoggedNavbarLeftAction';
import { LoggedNavbarRightAction } from './LoggedNavbarRightAction';
import { NavbarLogo } from './NavbarLogo';
import type { NavbarStyles } from './Cpagelayout.styles';
interface LoggedNavbarProps {
    dataElScope: DataElScope;
    authError: boolean;
    isAnyModalOpen: boolean;
    isCenterModalOpen: boolean;
    isLandingPage: boolean;
    isMobile: boolean;
    isScrolling: boolean;
    isSessionsModalOpen: boolean;
    isUserMenuModalOpen: boolean;
    navbarStyles: NavbarStyles;
    onHamburgerClick: () => void;
    onLogoClick: MouseEventHandler<HTMLAnchorElement>;
    onModalClose: () => void;
    onProfileClick: () => void;
    profileImageSrc?: string;
}
interface BuildLoggedNavbarWrapperStyleInput {
    isCenterModalOpen: boolean;
    isLandingPage: boolean;
}
interface BuildLoggedNavbarWrapperAnimationInput {
    isCenterModalOpen: boolean;
    isLandingPage: boolean;
    isScrolling: boolean;
}
interface GetLoggedLogoSizeInput {
    isLandingPage: boolean;
    isMobile: boolean;
}
interface CenterModalPointerEventsInput {
    isCenterModalOpen: boolean;
}
const LOGGED_NAVBAR_HIDDEN_ANIMATION = {
    opacity: 0,
    y: -8,
};
const LOGGED_NAVBAR_VISIBLE_ANIMATION = {
    opacity: 1,
    y: 0,
};
const LOGGED_NAVBAR_SCROLLING_ANIMATION = {
    opacity: 0.3,
    y: 0,
};
function getCenterModalPointerEvents({ isCenterModalOpen, }: CenterModalPointerEventsInput): 'none' | 'auto' {
    return isCenterModalOpen ? CSS_POINTER_EVENTS_NONE : CSS_POINTER_EVENTS_AUTO;
}
function buildLoggedNavbarWrapperStyle({ isCenterModalOpen, isLandingPage, }: BuildLoggedNavbarWrapperStyleInput): CSSProperties {
    const pointerEvents = getCenterModalPointerEvents({ isCenterModalOpen });
    if (!isLandingPage) {
        return {
            position: 'relative',
            zIndex: zIndex.navbar,
        };
    }
    return {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: pxToRem(NAVBAR_HEIGHT),
        zIndex: zIndex.navbar,
        pointerEvents,
    };
}
function buildLoggedNavbarWrapperAnimation({ isCenterModalOpen, isLandingPage, isScrolling, }: BuildLoggedNavbarWrapperAnimationInput): {
    opacity: number;
    y: number;
} {
    if (!isLandingPage) {
        return LOGGED_NAVBAR_VISIBLE_ANIMATION;
    }
    if (isCenterModalOpen) {
        return LOGGED_NAVBAR_HIDDEN_ANIMATION;
    }
    if (isScrolling) {
        return LOGGED_NAVBAR_SCROLLING_ANIMATION;
    }
    return LOGGED_NAVBAR_VISIBLE_ANIMATION;
}
function getLoggedLogoSize({ isLandingPage, isMobile, }: GetLoggedLogoSizeInput): number {
    if (!isMobile) {
        return 54;
    }
    return isLandingPage ? 46 : 58;
}
export function LoggedNavbar({ dataElScope, authError, isAnyModalOpen, isCenterModalOpen, isLandingPage, isMobile, isScrolling, isSessionsModalOpen, isUserMenuModalOpen, navbarStyles, onHamburgerClick, onLogoClick, onModalClose, onProfileClick, profileImageSrc, }: LoggedNavbarProps): ReactElement {
    return (
      <motion.div data-el={dataEl(dataElScope, 'LoggedNavbar/Root')} initial={{ opacity: 0, y: -64 }} animate={buildLoggedNavbarWrapperAnimation({
            isCenterModalOpen,
            isLandingPage,
            isScrolling,
        })} exit={{ opacity: 0, y: -64 }} transition={{
            duration: 0.4,
            ease: [0.4, 0, 0.2, 1],
        }} style={buildLoggedNavbarWrapperStyle({
            isCenterModalOpen,
            isLandingPage,
        })}>
        <motion.nav animate={{
            backdropFilter: isAnyModalOpen ? 'blur(10px)' : 'blur(0px)',
        }} transition={{
            duration: 0.2,
            ease: [0.4, 0, 0.2, 1],
        }} style={navbarStyles.base}>
          <div style={navbarStyles.left}>
            <LoggedNavbarLeftAction isMobile={isMobile} isSessionsModalOpen={isSessionsModalOpen} onHamburgerClick={onHamburgerClick} onModalClose={onModalClose}/>
          </div>

          <div style={navbarStyles.center}>
            <NavbarLogo isMobile={isMobile} linkStyle={navbarStyles.logoLink} logoSize={getLoggedLogoSize({ isLandingPage, isMobile })} motionStyle={navbarStyles.logoMotion} onClick={onLogoClick}/>
          </div>

          <div style={navbarStyles.right}>
            <LoggedNavbarRightAction authError={authError} isMobile={isMobile} isUserMenuModalOpen={isUserMenuModalOpen} onModalClose={onModalClose} onProfileClick={onProfileClick} profileImageSrc={profileImageSrc}/>
          </div>
        </motion.nav>
      </motion.div>
    );
}
